import moment from "moment";
import i18n from "../i18n";

const PlanManager = (app) => {
  app
    .post(
      "/plan-manager/status",
      async ({ api, assert, flash, req, res, locals }) => {
        const {
          body: { id, status },
        } = req;

        assert(status, 400, i18n.t("coaching-portal:planManagerActions.statusRequired"));
        assert(id, 400, i18n.t("coaching-portal:planManagerActions.planIdRequired"));

        let bodyToSend = { id, active: status !== "unpublish" };
        if (locals.activeUser.organizationId) {
          bodyToSend = {
            ...bodyToSend,
            organizationUser: locals.activeUserToRequest._id,
          };
        }

        await api(`coach-plan-manager`, {
          method: "PUT",
          body: bodyToSend,
        });

        flash("success", status === "unpublish" ? i18n.t("coaching-portal:planManagerActions.successfullyUnpublished") : i18n.t("coaching-portal:planManagerActions.successfullyPublished"));

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/plan-manager/preferred",
      async ({ api, assert, flash, req, res, locals }) => {
        const {
          body: { id, preferred, plans },
        } = req;

        assert(id, 400, i18n.t("coaching-portal:planManagerActions.planIdRequired"));
        assert(plans, 400, i18n.t("coaching-portal:planManagerActions.listOfPlansRequired"));

        if (!preferred) {
          const hasPreferred = plans.some(
            (plan) => plan.metadata?.preferred === "true"
          );

          assert(!hasPreferred, 400, i18n.t("coaching-portal:planManagerActions.alreadyPreferredPlan"));
        }

        let bodyToSend = { id, preferred: !preferred };
        if (locals.activeUser.organizationId) {
          bodyToSend = {
            ...bodyToSend,
            organizationUser: locals.activeUserToRequest._id,
          };
        }
        await api(`coach-plan-manager`, {
          method: "PUT",
          body: bodyToSend,
        });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullyEdited"));

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/plan-manager/reorder",
      async ({ api, assert, flash, req, res, locals }) => {
        const {
          body: { order, type },
        } = req;

        assert(order?.length, 400, i18n.t("coaching-portal:planManagerActions.listOfPlansRequired"));

        let bodyToSend = { order, type };
        if (locals.activeUser.organizationId) {
          bodyToSend = {
            ...bodyToSend,
            organizationUser: locals.activeUserToRequest._id,
          };
        }
        await api(`coach-plan-manager`, {
          method: "PUT",
          body: bodyToSend,
        });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullySaved"));

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/plan-manager/private",
      async ({ api, assert, flash, req, res, locals }) => {
        const {
          body: { id, private: isPrivate },
        } = req;

        assert(id, 400, "Plan id is required");

        let bodyToSend = {
          id,
          private: !isPrivate,
        };
        if (locals.activeUser.organizationId) {
          bodyToSend = {
            ...bodyToSend,
            organizationUser: locals.activeUserToRequest._id,
          };
        }
        await api(`coach-plan-manager`, {
          method: "PUT",
          body: bodyToSend,
        });

        flash("success", `Successfully edited`);

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/plan-manager/save",
      async ({ api, assert, flash, req, res, locals }) => {
        const { body } = req;

        // A one-off pass is a Stripe one-time price: no cadence, and for a day
        // pass no app access either, so neither field is asked for or required.
        const isOneOff = body.billing_type === "one_off";
        const isDayPass = isOneOff && body.pass_type === "day";

        if (!body.id) {
          assert(body.price, 400, i18n.t("coaching-portal:planManagerActions.priceRequired"));
          assert(body.currency, 400, i18n.t("coaching-portal:planManagerActions.currencyRequired"));
          if (isOneOff) {
            assert(body.pass_type, 400, i18n.t("coaching-portal:planManagerActions.passTypeRequired"));
          } else {
            assert(body.interval, 400, i18n.t("coaching-portal:planManagerActions.intervalRequired"));
          }
          if (body.type === "press" && !isDayPass) {
            assert(body.access, 400, i18n.t("coaching-portal:planManagerActions.userAccessRequired"));
          }
        }

        if (body.type === "press") {
          assert(body.nickname, 400, i18n.t("coaching-portal:planManagerActions.nameRequired"));
          assert(body.feature_1, 400, i18n.t("coaching-portal:planManagerActions.featureRequired"));
        }

        if (!body.facilikey) body.facilikey = null;
        if (!body.tax_rate) body.tax_rate = null;

        if (locals.activeUser.organizationId) {
          body.organizationUser = locals.activeUserToRequest._id;
        }

        await api(`coach-plan-manager`, {
          method: body.id ? "PUT" : "POST",
          body,
        });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullySaved"));

        res.refreshWith(
          { modal: "", plan: "", type: "" },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/plan-manager/add-trial",
      async ({ api, assert, flash, req, res, locals }) => {
        const { body } = req;

        if (body.hasFreeTrial) {
          assert(body.trial_days, 400, i18n.t("coaching-portal:planManagerActions.freeTrialDaysRequired"));
        } else {
          body.trial_days = null;
        }

        if (locals.activeUser.organizationId) {
          body.organizationUser = locals.activeUserToRequest._id;
        }

        await api(`coach-plan-manager`, {
          method: "PUT",
          body,
        });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullySaved"));

        res.refreshWith(
          { modal: "", plan: "", type: "" },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/plan-manager/add-minimum-term",
      async ({ api, assert, flash, req, res, locals }) => {
        const { body } = req;

        if (body.hasMinimumTerm) {
          assert(body.minimum_term, 400, i18n.t("coaching-portal:planManagerActions.minimumTermRequired"));
        } else {
          body.minimum_term = null;
        }

        if (locals.activeUser.organizationId) {
          body.organizationUser = locals.activeUserToRequest._id;
        }

        console.log({ body });

        await api(`coach-plan-manager`, {
          method: "PUT",
          body,
        });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullySaved"));

        res.refreshWith(
          { modal: "", plan: "", type: "" },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/plan-manager/create-coupon",
      async ({ api, flash, req, res, locals }) => {
        const { body } = req;
        if (body.redeem_by) {
          body.redeem_by = moment(body.redeem_by).unix();
        }
        if (locals.activeUser.organizationId) {
          body.organizationUser = locals.activeUserToRequest._id;
        }

        const couponColors = [
          ["#00B4DB", "#0083B0"],
          ["#BC4E9C", "#F80759"],
          ["#FF9068", "#FF4B1F"],
          ["#734B6D", "#42275A"],
          ["#8E2DE2", "#4A00E0"],
          ["#34E89E", "#0F3443"],
          ["#C11E38", "#741020"],
          ["#F7BA2C", "#EA5459"],
        ];

        const randomNumber = Math.floor(Math.random() * 8);
        body.colors = couponColors[randomNumber];

        await api(`stripe-coupon`, {
          method: "POST",
          body,
        });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullyCreated"));

        res.backDontScroll();
      }
    )
    .get(
      "/plan-manager/delete-coupon/:id",
      async ({ api, req, res, flash, locals }) => {
        const { id } = req.params;
        let query = `?id=${id}`;
        if (locals.activeUser.organizationId) {
          query += `&organizationUser=${locals.activeUserToRequest._id}`;
        }

        await api(`stripe-coupon${query}`, { method: "DELETE" });

        flash("success", i18n.t("coaching-portal:planManagerActions.successfullyDeleted"));

        res.backDontScroll();
      }
    );
};

export default PlanManager;
